import {
  CALIBRATION_TIERS,
  estimatedCustomFps,
  estimatedStageFps,
  recommendCalibration,
  RENDER_SCALES,
  saveCalibration,
  smoothRenderScale,
  startScaleForTier,
  tierFeatureDefaults,
  tierSupportsFeature,
  type CalibrationFeatures,
  type CalibrationStage,
  type CalibrationTier,
  type GraphicsCalibration,
} from '../graphics-calibration.js';
import type { BenchmarkPreview, BenchmarkProgress, GraphicsBenchmark } from '../graphics-benchmark.js';
import { acquireGpuExclusive } from '../gpu-exclusive.js';
import { formatNumber, intlLocale, t } from '../i18n/index.js';
import { applyCalibrationToSettings, type GameSettings } from '../settings.js';
import { escapeHtml } from './html.js';

/**
 * Full-screen device check. The test drive itself is the backdrop; a glass
 * panel shows each quality tier being timed, then the recommendation. The
 * result is editable in place: pick another tier or switch single effects
 * and the drive behind re-renders with exactly that setup. Skippable at any
 * point, repeatable from the result, the menu's GRAPHICS panel and
 * Settings › Video, which can also reopen it to adjust without retesting.
 */

export interface PerformanceTestHost {
  touchDevice: boolean;
  /** The live settings the panel starts from. */
  settings(): GameSettings;
  /** Persist and apply edited settings immediately. */
  update(next: GameSettings): void;
  /** The player skipped before a result was available. */
  skipped(): void;
  /** The overlay is gone. */
  closed(): void;
  sound?(kind: 'tick' | 'confirm' | 'back' | 'toggle'): void;
}

export interface PerformanceTestOptions {
  /** Open on a stored result to adjust it, without running the test again. */
  review?: GraphicsCalibration;
}

export interface PerformanceTestHandle {
  close(): void;
}

const TIER_LABEL: Record<CalibrationTier, string> = {
  low: 'LOW',
  medium: 'MEDIUM',
  high: 'HIGH',
  ultra: 'ULTRA',
};

type EditableFeature = keyof CalibrationFeatures;
const GRAPHICS_KEYS = ['graphicsQuality', 'bloom', 'shadows', 'screenDistortion', 'screenSpaceLighting', 'carReflections', 'frameRateTarget'] as const;

let active: PerformanceTestHandle | null = null;

export function performanceTestOpen(): boolean {
  return active !== null;
}

export function openPerformanceTest(host: PerformanceTestHost, options: PerformanceTestOptions = {}): PerformanceTestHandle {
  active?.close();
  const releaseGpu = acquireGpuExclusive();
  const previousFocus = document.activeElement as HTMLElement | null;
  const element = document.createElement('div');
  element.className = 'perf-test';
  element.dataset.state = 'running';
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-modal', 'true');
  element.setAttribute('aria-labelledby', 'perf-test-title');
  element.innerHTML = shellHtml();
  document.body.append(element);
  document.documentElement.classList.add('perf-test-active');
  requestAnimationFrame(() => element.classList.add('is-visible'));

  const stage = element.querySelector<HTMLElement>('.perf-test-stage')!;
  const panel = element.querySelector<HTMLElement>('.perf-test-panel')!;
  let benchmark: GraphicsBenchmark | null = null;
  let controller: AbortController | null = null;
  let result: GraphicsCalibration | null = null;
  let reviewing = false;
  let closed = false;

  const close = (): void => {
    if (closed) return;
    closed = true;
    controller?.abort();
    window.removeEventListener('keydown', onKey, true);
    element.classList.remove('is-visible');
    element.classList.add('is-leaving');
    document.documentElement.classList.remove('perf-test-active');
    const finish = (): void => {
      benchmark?.dispose();
      benchmark = null;
      element.remove();
      releaseGpu();
      if (active === handle) active = null;
      host.closed();
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) finish();
    else window.setTimeout(finish, 320);
  };

  const skip = (): void => {
    if (closed) return;
    host.sound?.('back');
    if (!result) host.skipped();
    close();
  };

  const ensureBenchmark = async (signal: AbortSignal): Promise<GraphicsBenchmark | null> => {
    if (benchmark) return benchmark;
    const module = await import('../graphics-benchmark.js');
    if (signal.aborted || closed) return null;
    benchmark = new module.GraphicsBenchmark(stage);
    return benchmark;
  };

  /** Re-renders the editable result and points the backdrop at the new setup. */
  const paintResult = (focusKey?: string): void => {
    if (!result) return;
    const settings = host.settings();
    const view = resultView(result, settings);
    panel.innerHTML = resultHtml(result, view, reviewing);
    paintTiers(element, result.stages, CALIBRATION_TIERS.filter((tier) => !result!.stages.some((entry) => entry.tier === tier)), null, view.tier);
    if (focusKey) panel.querySelector<HTMLElement>(`[data-focus-key="${focusKey}"]`)?.focus({ preventScroll: true });
    const preview: BenchmarkPreview = {
      tier: view.tier,
      renderScale: view.renderScale ?? 1,
      bloom: settings.bloom,
      shadows: settings.shadows,
      screenSpaceLighting: settings.screenSpaceLighting,
      carReflections: settings.carReflections,
      frameRateTarget: settings.frameRateTarget,
    };
    const current = benchmark;
    if (!current) return;
    void current.preview(preview).then(() => {
      if (!closed && element.dataset.phase !== 'done') element.dataset.phase = 'done';
    });
  };

  const showResult = (calibration: GraphicsCalibration, review: boolean): void => {
    result = calibration;
    reviewing = review;
    element.dataset.state = 'result';
    element.querySelector<HTMLElement>('.perf-test-skip')!.hidden = true;
    paintMeta(element, calibration.renderer, calibration.viewport);
    paintResult('continue');
  };

  const edit = (next: GameSettings, focusKey: string): void => {
    host.update(next);
    paintResult(focusKey);
  };

  const chooseTier = (tier: CalibrationTier): void => {
    if (!result) return;
    const settings = host.settings();
    host.sound?.('tick');
    // The recommended tier means AUTO again: measured effects and start scale.
    edit(tier === result.tier
      ? applyCalibrationToSettings(settings, result)
      : { ...settings, graphicsQuality: tier, adaptivePerformance: true, ...tierFeatureDefaults(tier) }, `tier-${tier}`);
  };

  const toggleFeature = (feature: EditableFeature | 'frameRateTarget'): void => {
    if (!result) return;
    const settings = host.settings();
    host.sound?.('toggle');
    if (feature === 'frameRateTarget') {
      edit({ ...settings, frameRateTarget: settings.frameRateTarget === 'battery' ? 'smooth' : 'battery' }, `feature-${feature}`);
      return;
    }
    edit({ ...settings, [feature]: !settings[feature] }, `feature-${feature}`);
  };

  const start = async (): Promise<void> => {
    result = null;
    element.dataset.state = 'running';
    delete element.dataset.phase;
    panel.innerHTML = runningHtml();
    element.querySelector<HTMLElement>('.perf-test-skip')!.hidden = false;
    paintTiers(element, [], [], null, null);
    controller?.abort();
    const run = new AbortController();
    controller = run;
    try {
      const current = await ensureBenchmark(run.signal);
      if (!current) return;
      paintMeta(element, current.rendererName, current.viewport);
      const outcome = await current.run((progress) => paintProgress(element, progress), run.signal);
      if (run.signal.aborted || closed) return;
      const calibration = recommendCalibration({
        stages: outcome.stages,
        cpuMs: outcome.cpuMs,
        touchDevice: host.touchDevice,
        renderer: friendlyGpuName(outcome.renderer),
        viewport: outcome.viewport,
        now: Date.now(),
      });
      saveCalibration(calibration);
      host.update(applyCalibrationToSettings(host.settings(), calibration));
      host.sound?.('confirm');
      showResult(calibration, false);
    } catch (error) {
      if (run.signal.aborted || closed || (error instanceof DOMException && error.name === 'AbortError')) return;
      element.dataset.state = 'error';
      panel.innerHTML = errorHtml();
      panel.querySelector<HTMLButtonElement>('[data-perf-continue]')?.focus({ preventScroll: true });
    }
  };

  const review = async (calibration: GraphicsCalibration): Promise<void> => {
    element.dataset.phase = 'preparing';
    showResult(calibration, true);
    const run = new AbortController();
    controller = run;
    try {
      if (await ensureBenchmark(run.signal)) paintResult('continue');
    } catch {
      // No 3D context: the panel still edits the settings without a preview.
      element.dataset.phase = 'done';
    }
  };

  element.addEventListener('click', (event) => {
    const target = (event.target as HTMLElement).closest<HTMLButtonElement>('button');
    if (!target || target.disabled) return;
    if (target.matches('[data-perf-skip]')) skip();
    else if (target.matches('[data-perf-continue]')) {
      host.sound?.('confirm');
      // Without a 3D context the test can never run: do not offer it again.
      if (!result) host.skipped();
      close();
    } else if (target.matches('[data-perf-retry]')) {
      host.sound?.('tick');
      void start();
    } else if (target.dataset.perfTier) {
      chooseTier(target.dataset.perfTier as CalibrationTier);
    } else if (target.dataset.perfFeature) {
      toggleFeature(target.dataset.perfFeature as EditableFeature | 'frameRateTarget');
    } else if (target.matches('[data-perf-reset]') && result) {
      host.sound?.('tick');
      edit(applyCalibrationToSettings(host.settings(), result), 'continue');
    }
  });

  // The overlay owns the keyboard: menu shortcuts and driving keys stay quiet.
  const onKey = (event: KeyboardEvent): void => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.code === 'Escape') {
      event.preventDefault();
      event.stopImmediatePropagation();
      skip();
      return;
    }
    if (event.code === 'KeyR' && element.dataset.state !== 'running') {
      event.preventDefault();
      event.stopImmediatePropagation();
      host.sound?.('tick');
      void start();
      return;
    }
    if (result && /^Digit[1-4]$/.test(event.code)) {
      // 1-4 pick a tier straight from the keyboard.
      event.preventDefault();
      event.stopImmediatePropagation();
      chooseTier(CALIBRATION_TIERS[Number(event.code.slice(5)) - 1]!);
      return;
    }
    if (event.code === 'Tab' || event.code === 'Enter' || event.code === 'NumpadEnter' || event.code === 'Space') {
      // Let focused buttons work but keep focus inside the dialog.
      if (!element.contains(document.activeElement)) {
        event.preventDefault();
        element.querySelector<HTMLElement>('[data-perf-continue], [data-perf-skip]:not([hidden])')?.focus({ preventScroll: true });
      }
      event.stopImmediatePropagation();
      return;
    }
    event.stopImmediatePropagation();
  };
  window.addEventListener('keydown', onKey, true);

  const handle: PerformanceTestHandle = { close };
  active = handle;
  if (options.review) {
    void review(options.review);
  } else {
    element.querySelector<HTMLElement>('[data-perf-skip]')?.focus({ preventScroll: true });
    void start();
  }
  return handle;
}

interface ResultView {
  tier: CalibrationTier;
  recommended: boolean;
  modified: boolean;
  renderScale: number | null;
  fps: number | null;
  battery: boolean;
  features: CalibrationFeatures;
}

function resultView(calibration: GraphicsCalibration, settings: GameSettings): ResultView {
  const tier = settings.graphicsQuality === 'auto' ? calibration.tier : settings.graphicsQuality;
  const recommendedSettings = applyCalibrationToSettings(settings, calibration);
  const modified = GRAPHICS_KEYS.some((key) => settings[key] !== recommendedSettings[key]);
  const features: CalibrationFeatures = {
    bloom: settings.bloom,
    shadows: settings.shadows,
    screenDistortion: settings.screenDistortion,
    screenSpaceLighting: settings.screenSpaceLighting,
    carReflections: settings.carReflections,
  };
  const stage = calibration.stages.find((entry) => entry.tier === tier);
  const renderScale = startScaleForTier(calibration, tier);
  const fps = stage && renderScale !== null ? estimatedCustomFps(stage, renderScale, features, settings.frameRateTarget) : null;
  return { tier, recommended: tier === calibration.tier, modified, renderScale, fps, battery: settings.frameRateTarget === 'battery', features };
}

function shellHtml(): string {
  return `
    <div class="perf-test-stage"></div>
    <div class="perf-test-shade" aria-hidden="true"></div>
    <div class="perf-test-scan" aria-hidden="true"></div>
    <header class="perf-test-top">
      <span class="perf-test-brand"><b>//</b> NIGHTSHIFT</span>
      <span class="perf-test-chip"><i aria-hidden="true"></i>${t('SYSTEM CHECK')}</span>
      <button type="button" class="perf-test-skip" data-perf-skip data-pad-back data-pad-back-label="${t('SKIP')}">${t('SKIP TEST')}<kbd>Esc</kbd></button>
    </header>
    <section class="perf-test-panel" aria-live="polite"></section>
    <aside class="perf-test-side">
      <p class="perf-test-side-label">${t('QUALITY TIERS')}</p>
      <ol class="perf-test-tiers">
        ${CALIBRATION_TIERS.map((tier, index) => `
          <li data-tier="${tier}" data-state="pending" style="--tier-index:${index}">
            <span class="perf-tier-name"><em>${String(index + 1).padStart(2, '0')}</em>${t(TIER_LABEL[tier])}</span>
            <span class="perf-tier-meter" aria-hidden="true"><i></i><b></b></span>
            <span class="perf-tier-value">—</span>
          </li>`).join('')}
      </ol>
      <dl class="perf-test-meta">
        <div><dt>${t('GPU')}</dt><dd data-perf-gpu>—</dd></div>
        <div><dt>${t('DISPLAY')}</dt><dd data-perf-display>—</dd></div>
      </dl>
    </aside>`;
}

function runningHtml(): string {
  return `
    <p class="perf-test-eyebrow">${t('DEVICE CALIBRATION · A FEW SECONDS')}</p>
    <h1 id="perf-test-title">${t('TUNING THE NIGHT')}<em>${t('FOR YOUR DEVICE')}</em></h1>
    <p class="perf-test-lede">${t('A short test drive renders lights, shadows, reflections and bloom at every quality tier. Nightshift then switches on exactly what your device holds at 60 FPS.')}</p>
    <div class="perf-test-progress" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-label="${t('Test progress')}"><i></i></div>
    <p class="perf-test-status"><span data-perf-status>${t('Preparing shaders…')}</span><b data-perf-percent>0%</b></p>`;
}

function resultHtml(calibration: GraphicsCalibration, view: ResultView, review: boolean): string {
  const features: { key: EditableFeature | 'frameRateTarget'; label: string; detail: string; on: boolean; supported: boolean }[] = [
    { key: 'bloom', label: t('BLOOM'), detail: t('Neon glow'), on: view.features.bloom, supported: tierSupportsFeature(view.tier, 'bloom') },
    { key: 'shadows', label: t('DYNAMIC SHADOWS'), detail: t('Moon and lamp shadows'), on: view.features.shadows, supported: tierSupportsFeature(view.tier, 'shadows') },
    { key: 'screenSpaceLighting', label: t('AMBIENT OCCLUSION'), detail: t('Plus wet-road reflections'), on: view.features.screenSpaceLighting, supported: tierSupportsFeature(view.tier, 'screenSpaceLighting') },
    { key: 'carReflections', label: t('CAR REFLECTIONS'), detail: t('Live city on the paint'), on: view.features.carReflections, supported: tierSupportsFeature(view.tier, 'carReflections') },
    { key: 'screenDistortion', label: t('HEAT HAZE'), detail: t('Fire and blast shimmer'), on: view.features.screenDistortion, supported: tierSupportsFeature(view.tier, 'screenDistortion') },
  ];
  const battery = view.battery;
  const eyebrow = review
    ? t('YOUR GRAPHICS · TESTED {date}', { date: new Date(calibration.measuredAt).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short' }) })
    : t('CALIBRATION COMPLETE · SAVED ON THIS DEVICE');
  const fpsWarning = view.fps !== null && view.fps < (battery ? 28 : 55);
  return `
    <p class="perf-test-eyebrow">${eyebrow}</p>
    <h1 id="perf-test-title">${t('YOUR SETUP')}<em>${t(TIER_LABEL[view.tier])}${view.modified ? `<span class="perf-custom-badge">${t('CUSTOM')}</span>` : ''}</em></h1>
    <div class="perf-tier-picker" role="radiogroup" aria-label="${t('QUALITY PRESET')}">
      ${CALIBRATION_TIERS.map((tier, index) => {
        const stage = calibration.stages.find((entry) => entry.tier === tier);
        const scale = startScaleForTier(calibration, tier);
        const fps = stage && scale !== null ? estimatedStageFps(stage, scale) : null;
        const holds = stage ? smoothRenderScale(stage) !== null : false;
        return `<button type="button" role="radio" aria-checked="${tier === view.tier}" data-perf-tier="${tier}" data-focus-key="tier-${tier}" data-holds="${holds}" style="--tier-index:${index}">
          <b>${t(TIER_LABEL[tier])}</b>
          <small>${fps === null ? t('NOT TESTED') : `${Math.min(60, Math.round(fps))} FPS`}</small>
          ${tier === calibration.tier ? `<i class="perf-tier-recommended">${t('RECOMMENDED')}</i>` : ''}
        </button>`;
      }).join('')}
    </div>
    <dl class="perf-result-stats">
      <div data-warning="${fpsWarning}"><dt>${t('EXPECTED')}</dt><dd>${view.fps !== null ? `${view.fps}<small>FPS</small>` : '—'}</dd></div>
      <div><dt>${t('RESOLUTION')}</dt><dd>${view.renderScale !== null ? `${Math.round(view.renderScale * 100)}<small>%</small>` : '—'}</dd></div>
      <div><dt>${t('GPU SCORE')}</dt><dd>${formatScore(calibration.gpuScore)}</dd></div>
      <div><dt>${t('CPU SCORE')}</dt><dd>${formatScore(calibration.cpuScore)}</dd></div>
    </dl>
    <div class="perf-features" role="group" aria-label="${t('Effects')}">
      ${features.map((feature, index) => `
        <button type="button" role="switch" aria-checked="${feature.on && feature.supported}" data-perf-feature="${feature.key}" data-focus-key="feature-${feature.key}" ${feature.supported ? '' : `disabled title="${escapeHtml(feature.key === 'screenSpaceLighting' || feature.key === 'carReflections' ? t('Available on HIGH and ULTRA') : t('Not available on LOW'))}"`} style="--feature-index:${index}">
          <i aria-hidden="true">${feature.on && feature.supported ? CHECK_ICON : CROSS_ICON}</i>
          <span><b>${feature.label}</b><small>${feature.supported ? feature.detail : feature.key === 'screenSpaceLighting' || feature.key === 'carReflections' ? t('HIGH and ULTRA only') : t('Not on LOW')}</small></span>
        </button>`).join('')}
      <button type="button" role="switch" aria-checked="${!battery}" data-perf-feature="frameRateTarget" data-focus-key="feature-frameRateTarget" class="perf-feature-frame" style="--feature-index:5">
        <i aria-hidden="true">${battery ? BATTERY_ICON : BOLT_ICON}</i>
        <span><b>${t('FRAME TARGET')}</b><small>${battery ? t('Stable 30 FPS') : t('Smooth 60 FPS')}</small></span>
      </button>
    </div>
    <p class="perf-test-note">${view.modified
      ? `${t('Your own setup is saved and shown live behind this panel.')} <button type="button" class="perf-test-link" data-perf-reset data-focus-key="reset">${t('Back to recommended')}</button>`
      : t('Pick a tier or switch an effect to override the suggestion; the drive behind shows it live. Adaptive performance keeps fine-tuning resolution in races.')}</p>
    <div class="perf-test-actions">
      <button type="button" class="perf-test-secondary" data-perf-retry data-focus-key="retry">${t('TEST AGAIN')}<kbd>R</kbd></button>
      <button type="button" class="perf-test-primary" data-perf-continue data-focus-key="continue" data-pad-default data-pad-back data-pad-back-label="${t('CONTINUE')}">${review ? t('DONE') : t('LET’S RACE')}<b aria-hidden="true">→</b></button>
    </div>`;
}

function errorHtml(): string {
  return `
    <p class="perf-test-eyebrow">${t('CALIBRATION UNAVAILABLE')}</p>
    <h1 id="perf-test-title">${t('NO TEST DRIVE')}<em>${t('THIS TIME')}</em></h1>
    <p class="perf-test-lede">${t('The browser did not give the test a 3D context. Nightshift keeps its automatic estimate and still adapts during races.')}</p>
    <div class="perf-test-actions">
      <button type="button" class="perf-test-secondary" data-perf-retry>${t('TRY AGAIN')}<kbd>R</kbd></button>
      <button type="button" class="perf-test-primary" data-perf-continue data-pad-default data-pad-back data-pad-back-label="${t('CONTINUE')}">${t('CONTINUE')}<b aria-hidden="true">→</b></button>
    </div>`;
}

function paintMeta(root: HTMLElement, renderer: string, viewport: { width: number; height: number; devicePixelRatio: number }): void {
  const gpu = root.querySelector<HTMLElement>('[data-perf-gpu]');
  if (gpu) {
    const name = friendlyGpuName(renderer);
    gpu.textContent = name || t('Unknown GPU');
    gpu.title = renderer;
  }
  const display = root.querySelector<HTMLElement>('[data-perf-display]');
  if (display) {
    const width = Math.round(viewport.width * viewport.devicePixelRatio);
    const height = Math.round(viewport.height * viewport.devicePixelRatio);
    display.textContent = `${width} × ${height}${viewport.devicePixelRatio !== 1 ? ` · ${viewport.devicePixelRatio.toFixed(2).replace(/\.?0+$/, '')}×` : ''}`;
  }
}

function paintProgress(root: HTMLElement, progress: BenchmarkProgress): void {
  if (root.dataset.phase !== progress.phase) root.dataset.phase = progress.phase;
  const percent = Math.round(progress.progress * 100);
  const bar = root.querySelector<HTMLElement>('.perf-test-progress');
  if (bar) {
    bar.style.setProperty('--progress', `${percent}%`);
    bar.setAttribute('aria-valuenow', String(percent));
  }
  const percentLabel = root.querySelector<HTMLElement>('[data-perf-percent]');
  if (percentLabel) percentLabel.textContent = `${percent}%`;
  const status = root.querySelector<HTMLElement>('[data-perf-status]');
  if (status) {
    const text = progress.phase === 'preparing' ? t('Preparing shaders…')
      : progress.phase === 'warmup' ? t('Warming up the GPU…')
      : progress.phase === 'stage' && progress.tier ? t('Timing {tier} — lights, shadows, post-processing', { tier: t(TIER_LABEL[progress.tier]) })
      : progress.phase === 'cpu' ? t('Measuring the processor…')
      : t('Choosing your settings…');
    if (status.textContent !== text) status.textContent = text;
  }
  paintTiers(root, progress.completed, progress.skipped, progress.phase === 'stage' ? progress : null, null);
}

function paintTiers(
  root: HTMLElement,
  completed: readonly CalibrationStage[],
  skipped: readonly CalibrationTier[],
  live: BenchmarkProgress | null,
  chosen: CalibrationTier | null,
): void {
  for (const row of root.querySelectorAll<HTMLElement>('.perf-test-tiers li')) {
    const tier = row.dataset.tier as CalibrationTier;
    const stage = completed.find((entry) => entry.tier === tier);
    const value = row.querySelector<HTMLElement>('.perf-tier-value')!;
    let state = 'pending';
    let fps: number | null = null;
    let scale: number | null = null;
    if (stage) {
      const holds = smoothRenderScale(stage);
      state = holds === null ? 'fail' : 'pass';
      // A tier that misses 60 is shown at the softest start the race would use.
      scale = holds ?? RENDER_SCALES[RENDER_SCALES.length - 1]!;
      fps = estimatedStageFps(stage, scale);
    } else if (skipped.includes(tier)) {
      state = 'skipped';
    } else if (live?.tier === tier) {
      state = 'active';
      if (live.liveMs !== null) fps = estimatedStageFps({ tier, frameMs: live.liveMs, pixelRatio: 1, pixels: 1 });
    }
    if (chosen === tier) state = 'chosen';
    if (row.dataset.state !== state) row.dataset.state = state;
    row.style.setProperty('--fill', fps === null ? '0' : String(Math.min(1, fps / 120)));
    const label = state === 'skipped' ? t('SKIPPED') : fps === null ? '—' : `${fps >= 240 ? '240+' : Math.round(fps)} FPS`;
    const note = scale !== null && scale < 1 ? t('{scale}% RES', { scale: Math.round(scale * 100) }) : '';
    const html = note ? `${label}<small>${note}</small>` : label;
    if (value.innerHTML !== html) value.innerHTML = html;
  }
}

/** "ANGLE (Intel, Mesa Intel(R) Iris(R) Xe Graphics (TGL GT2), OpenGL ES 3.2)" → "Intel Iris Xe Graphics". */
export function friendlyGpuName(raw: string): string {
  let name = raw.trim();
  const angle = name.match(/^ANGLE \((.*)\)$/);
  if (angle?.[1]) {
    const parts = angle[1].split(',').map((part) => part.trim());
    name = parts[1] ?? parts[0] ?? name;
  }
  return name
    .replace(/\((?:R|TM)\)|™|®/gi, '')
    .replace(/^Mesa\s+/i, '')
    .replace(/\s*\((?:0x[0-9a-f]+|[A-Z]{2,4}\s?GT\d)\)/gi, '')
    .replace(/\s+Direct3D.*$/i, '')
    .replace(/\s+(?:vs|ps)_\d_\d.*$/i, '')
    .replace(/\s*\/.*$/, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

function formatScore(value: number): string {
  return value > 0 ? escapeHtml(formatNumber(value)) : '—';
}

const CHECK_ICON = '<svg viewBox="0 0 24 24"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>';
const CROSS_ICON = '<svg viewBox="0 0 24 24"><path d="M7 7l10 10M17 7 7 17"/></svg>';
const BOLT_ICON = '<svg viewBox="0 0 24 24"><path d="M13 3 6 13.5h5.5L10 21l7.5-11H12z"/></svg>';
const BATTERY_ICON = '<svg viewBox="0 0 24 24"><rect x="3.5" y="7.5" width="15" height="9" rx="1.5"/><path d="M21 10.5v3M6.5 10.5v3M9.5 10.5v3"/></svg>';
